/**
 * Hjelpestønad Wizard Configuration
 *
 * Complete checkbox specification from docs/FEATURES/Hjelpestonad-Wizard-Spec-v2.docx Section 3.
 * All labels and descriptions in Norwegian (bokmål).
 */

const hjelpestonadConfig = {
  type: 'hjelpestonad',
  displayName: 'Hjelpestønad',
  route: '/soknad/hjelpestonad',

  steps: [
    // =========================================================================
    // STEP 0: Intro + family-member picker (PR: person-picker)
    // Welcomes the parent and chooses WHO the søknad is about. Rendered by
    // IntroStep via the isIntroStep flag. Always present, always first.
    // =========================================================================
    {
      id: 'intro',
      isIntroStep: true,
      title: 'Velkommen',
      // The intro copy that the earlier copy PR intentionally left empty — it
      // lives here now. Rendered as the step subtitle.
      subtitle:
        'Nå vil du få ulike spørsmål om hjelpebehovet til den du skal søke om hjelpestønad for. ' +
        'Spørsmålene er laget spesielt ut ifra kriteriene som beskrives på Nav sine nettsider og i ' +
        'Rundskriv om hjelpestønad. Dette er for å sikre at du får med alt som er relevant i ' +
        'søknaden. Du kan krysse av for et punkt, selv om det skjer sporadisk.',
    },

    // =========================================================================
    // STEP 0b: Tier-2 confirm-facts (PR: person-picker)
    // Rendered ONLY when the picked subject is NOT the onboarded child
    // (subjectIsOnboardedChild === false). The wizard SKIPS this step entirely
    // for the onboarded child, so it stays at a fixed config index without
    // affecting Tier-1 flow. Rendered by SubjectConfirmStep.
    // =========================================================================
    {
      id: 'subjectConfirm',
      isSubjectConfirmStep: true,
      title: 'Bekreft opplysninger',
      instruction:
        'Fortell oss litt om {name}, så blir søknaden riktig. Vi bruker bare det du fyller inn her.',
    },

    // =========================================================================
    // STEP 1: Hjelpebehov om morgenen (10 items)
    // =========================================================================
    {
      id: 'morning',
      title: 'Hjelpebehov om morgenen',
      instruction: 'Kryss av for de situasjonene du kjenner deg igjen i. Du trenger ikke kjenne deg igjen i alle. Hvis du ønsker å utdype med eksempler, kan du fylle ut det nederst under "tilleggsopplysninger".',
      gateQuestion: null,
      checkboxItems: [
        {
          id: 'morning_waking',
          label: 'Vekking og oppvåkning',
          description: 'Barnet trenger ekstra hjelp til å våkne, mange påminnelser, eller emosjonell støtte for å stå opp',
        },
        {
          id: 'morning_hygiene',
          label: 'Personlig hygiene',
          description: 'Barnet trenger hjelp med å huske og gjennomføre stell, tannpuss, toalettbesøk',
        },
        {
          id: 'morning_dressing',
          label: 'Påkledning',
          description: 'Barnet trenger hjelp med å velge riktige klær (vær/årstid), påminnelser, eller fysisk hjelp',
        },
        {
          id: 'morning_breakfast',
          label: 'Frokost',
          description: 'Barnet trenger hjelp til å finne frem mat, påminnelser om å spise, eller tilpasset mat',
        },
        {
          id: 'morning_medication',
          label: 'Medisinering',
          description: 'Barnet trenger påminnelser eller fysisk hjelp med medisiner',
        },
        {
          id: 'morning_transitions',
          label: 'Forberedelser og overganger',
          description: 'Barnet trenger hjelp til å forberede seg på neste aktivitet og gjennomføre overganger',
        },
        {
          id: 'morning_emotions',
          label: 'Følelsesregulering',
          description: 'Barnet trenger hjelp til å regulere følelser og energinivå',
        },
        {
          id: 'morning_supervision',
          label: 'Tilsyn',
          description: 'Du må være i nærheten og tilse at barnet gjør det det skal gjennom morgenen',
        },
        {
          id: 'morning_shielding',
          label: 'Skjerming',
          description: 'Du må skjerme barnet og søsken fra hverandre for å unngå konflikter',
        },
        {
          id: 'morning_bound',
          label: 'Bundet som eneste voksen',
          description: 'Du er som regel alene om morgenrutinen og bundet til barnets behov',
        },
      ],
      hasHelperFields: true,
      hasFreeText: true,
      freeTextPlaceholder: 'Har du eksempler på hvordan dette påvirker hverdagen? Jo mer konkret du beskriver, jo bedre blir søknaden. Tips! Beskriv også hva som skjer hvis du ikke utfører hjelpen.',
    },

    // =========================================================================
    // STEP 2: Hjelpebehov om dagen (12 items)
    // =========================================================================
    {
      id: 'daytime',
      title: 'Hjelpebehov om dagen',
      instruction: 'Kryss av for de situasjonene du kjenner deg igjen i. Du trenger ikke kjenne deg igjen i alle. Hvis du ønsker å utdype med eksempler, kan du fylle ut det nederst under "tilleggsopplysninger".',
      gateQuestion: null,
      checkboxItems: [
        {
          id: 'daytime_absence',
          label: 'Skolefravær',
          description: 'Barnet har helt eller delvis (ufrivillig) skolefravær og trenger derfor ekstra med tilsyn og hjelp gjennom hele dagen, sammenlignet med jevnaldrende.',
          hasFollowUp: true,
          followUpType: 'yesno',
          followUpLabel: 'Er fraværet på grunn av diagnosen eller utfordringer den medfører?',
        },
        {
          id: 'daytime_hygiene',
          label: 'Personlig hygiene',
          description: 'Barnet trenger hjelp med stell og hygiene gjennom dagen',
        },
        {
          id: 'daytime_dressing',
          label: 'Påkledning',
          description: 'Barnet trenger hjelp med å kle seg om gjennom dagen',
        },
        {
          id: 'daytime_meals',
          label: 'Lunsj og middag',
          description: 'Barnet trenger hjelp med å spise, påminnelser, eller motivere for å få i seg mat',
        },
        {
          id: 'daytime_medication',
          label: 'Medisinering',
          description: 'Barnet trenger påminnelser eller hjelp med medisiner på dagtid',
        },
        {
          id: 'daytime_transitions',
          label: 'Forberedelser og overganger',
          description: 'Barnet trenger hjelp til overganger mellom aktiviteter',
        },
        {
          id: 'daytime_emotions',
          label: 'Følelsesregulering',
          description: 'Barnet trenger hjelp med å regulere følelser og energi gjennom dagen',
        },
        {
          id: 'daytime_supervision',
          label: 'Tilsyn og beredskap',
          description: 'Du må være tilgjengelig/i beredskap mens barnet er på skole eller SFO',
        },
        {
          id: 'daytime_shielding',
          label: 'Skjerming',
          description: 'Du må skjerme barnet og søsken fra konflikter gjennom dagen',
        },
        {
          id: 'daytime_bound',
          label: 'Bundet til barnets behov',
          description: 'Du er bundet til dette barnets hjelpebehov gjennom dagen',
        },
        {
          id: 'daytime_rest',
          label: 'Hvile og energiregulering etter skolen',
          description: 'Barnet trenger hjelp til å ta pauser og regulere energi etter skoledagen',
        },
        {
          id: 'daytime_activities',
          label: 'Fritidsaktiviteter',
          description: 'Barnet trenger ekstra tilsyn, følge, eller hjelp til å delta i fritidsaktiviteter',
        },
      ],
      hasHelperFields: true,
      hasFreeText: true,
      freeTextPlaceholder: 'Har du eksempler på hvordan dette påvirker hverdagen? Jo mer konkret du beskriver, jo bedre blir søknaden. Tips! Beskriv også hva som skjer hvis du ikke utfører hjelpen.',
    },

    // =========================================================================
    // STEP 3: Hjelpebehov om kvelden (11 items)
    // =========================================================================
    {
      id: 'evening',
      title: 'Hjelpebehov om kvelden',
      instruction: 'Kryss av for de situasjonene du kjenner deg igjen i. Du trenger ikke kjenne deg igjen i alle. Hvis du ønsker å utdype med eksempler, kan du fylle ut det nederst under "tilleggsopplysninger".',
      gateQuestion: null,
      checkboxItems: [
        {
          id: 'evening_chores',
          label: 'Husarbeid',
          description: 'Barnet kan ikke selvstendig bidra med vanlige oppgaver i husholdningen',
        },
        {
          id: 'evening_meal',
          label: 'Kveldsmat',
          description: 'Barnet trenger hjelp til å finne og lage mat, påminnelser, eller motivere for å spise',
        },
        {
          id: 'evening_medication',
          label: 'Medisinering',
          description: 'Barnet trenger påminnelser eller hjelp med medisiner om kvelden',
        },
        {
          id: 'evening_transitions',
          label: 'Forberedelser og overganger',
          description: 'Barnet trenger hjelp til overganger til nye aktiviteter på kvelden',
        },
        {
          id: 'evening_emotions',
          label: 'Følelsesregulering',
          description: 'Barnet trenger ekstra hjelp med følelser/energi om kvelden (spesielt når det er slitent)',
        },
        {
          id: 'evening_supervision',
          label: 'Tilsyn',
          description: 'Du må være i nærheten og tilse barnet gjennom kvelden',
        },
        {
          id: 'evening_shielding',
          label: 'Skjerming',
          description: 'Du må skjerme barnet og søsken fra konflikter om kvelden',
        },
        {
          id: 'evening_bound',
          label: 'Bundet som eneste voksen',
          description: 'Du er som regel alene om kveldsrutinen',
        },
        {
          id: 'evening_hygiene',
          label: 'Kveldsstell og hygiene',
          description: 'Barnet trenger hjelp med kveldsstell, dusjing, tannpuss',
        },
        {
          id: 'evening_dressing',
          label: 'Påkledning',
          description: 'Barnet trenger hjelp med å kle seg om til kvelden/natten',
        },
        {
          id: 'evening_bedtime',
          label: 'Legging og sovning',
          description: 'Barnet trenger voksenstøtte, faste rutiner eller tilstedeværelse for å sovne',
        },
      ],
      hasHelperFields: true,
      hasFreeText: true,
      freeTextPlaceholder: 'Har du eksempler på hvordan dette påvirker hverdagen? Jo mer konkret du beskriver, jo bedre blir søknaden. Tips! Beskriv også hva som skjer hvis du ikke utfører hjelpen.',
    },

    // =========================================================================
    // STEP 4: Hjelpebehov om natten (gate question + 7 items)
    // =========================================================================
    {
      id: 'night',
      title: 'Hjelpebehov om natten',
      instruction: 'Kryss av for de situasjonene du kjenner deg igjen i. Du trenger ikke kjenne deg igjen i alle. Hvis du ønsker å utdype med eksempler, kan du fylle ut det nederst under "tilleggsopplysninger".',
      gateQuestion: {
        text: 'Sover du og barnet ditt som regel hele natten?',
        skipOnYes: true,
        skipMessage: 'Fint! Vi går videre til neste del.',
      },
      checkboxItems: [
        {
          id: 'night_feeding',
          label: 'Ernæring og mat om natten',
          description: 'Barnet trenger hjelp med mat/drikke på natten',
        },
        {
          id: 'night_medication',
          label: 'Medisinering',
          description: 'Barnet trenger medisiner på natten',
        },
        {
          id: 'night_emotions',
          label: 'Følelsesregulering',
          description: 'Barnet trenger hjelp med følelser eller trenger en voksen i nærheten for trygghet',
        },
        {
          id: 'night_supervision',
          label: 'Tilsyn og skjerming',
          description: 'Du må tilse at barnet sover, og skjerme andre familiemedlemmer',
        },
        {
          id: 'night_bound',
          label: 'Bundet som eneste voksen',
          description: 'Du er som regel alene om nattevakter',
        },
        {
          id: 'night_hygiene',
          label: 'Hygiene på natten',
          description: 'Barnet trenger hjelp med toalettbesøk, stell eller skifting om natten',
        },
        {
          id: 'night_dressing',
          label: 'Av- og påkledning',
          description: 'Barnet trenger hjelp med klær om natten',
        },
      ],
      hasHelperFields: true,
      hasFreeText: true,
      freeTextPlaceholder: 'Har du eksempler på hvordan dette påvirker hverdagen? Jo mer konkret du beskriver, jo bedre blir søknaden. Tips! Beskriv også hva som skjer hvis du ikke utfører hjelpen.',
    },

    // =========================================================================
    // STEP 5: Støtte og inntektstap (special format with sections)
    // =========================================================================
    {
      id: 'support',
      title: 'Støtte og inntektstap',
      instruction: 'Fortell oss om eksisterende støtte og hvordan barnets behov påvirker din arbeidssituasjon.',
      gateQuestion: null,
      isSupportStep: true, // Flag for special rendering
      sections: [
        {
          id: 'public_support',
          title: 'Offentlig støtte',
          description: 'Mottar barnet noen av disse tjenestene?',
          checkboxItems: [
            {
              id: 'support_avlastning',
              label: 'Avlastning',
              description: 'Barnet får avlastning fra kommunen (gjelder ikke støttekontakt)',
              hasFollowUp: true,
              followUpLabel: 'Hvor mye?',
              followUpPlaceholder: 'f.eks. 10 timer per måned',
            },
            {
              id: 'support_bpa',
              label: 'Brukerstyrt personlig assistanse (BPA)',
              description: 'Barnet har BPA',
              hasFollowUp: true,
              followUpLabel: 'Hvor mye?',
              followUpPlaceholder: 'f.eks. 20 timer per uke',
            },
            {
              id: 'support_bolig',
              label: 'Bolig/institusjon',
              description: 'Barnet bor i bolig eller institusjon med ansatte',
              hasFollowUp: true,
              followUpLabel: 'Hvor mye?',
              followUpPlaceholder: 'f.eks. fulltid, eller X dager per uke',
            },
          ],
        },
        {
          id: 'work_impact',
          title: 'Tap av arbeidsinntekt',
          description: 'Har barnets hjelpebehov påvirket arbeidssituasjonen til én eller begge foreldrene?',
          yesNoItems: [
            {
              id: 'support_reduced_work',
              label: 'Redusert arbeidstid',
              question: 'Har én eller begge foreldrene redusert hvor mye de jobber for å hjelpe barnet?',
            },
            {
              id: 'support_income_loss',
              label: 'Tapt inntekt',
              question: 'Har én eller begge foreldrene tapt inntekt på grunn av barnets hjelpebehov?',
            },
          ],
        },
      ],
      checkboxItems: [], // Empty - uses sections instead
      hasHelperFields: false,
      hasFreeText: true,
      freeTextPlaceholder: 'Beskriv kort hvordan det har påvirket din arbeidssituasjon',
      freeTextLabel: 'Tilleggsopplysninger om arbeidssituasjon',
    },

    // =========================================================================
    // STEP 6: Tilleggsopplysninger (11 items)
    // =========================================================================
    {
      id: 'additional',
      title: 'Tilleggsopplysninger',
      instruction: 'Kryss av for de situasjonene du kjenner deg igjen i. Du trenger ikke kjenne deg igjen i alle. Hvis du ønsker å utdype med eksempler, kan du fylle ut det nederst under "tilleggsopplysninger".',
      gateQuestion: null,
      checkboxItems: [
        {
          id: 'additional_alone',
          label: 'Kan ikke være hjemme alene',
          description: 'Barnet kan ikke være hjemme alene over tid, sammenlignet med jevnaldrende, uten forberedelse og beredskap',
        },
        {
          id: 'additional_stimulation',
          label: 'Stimulering og opplæring',
          description: 'Barnet trenger ekstra stimulering fra voksen for å utvikle seg, utover det som er normalt for alderen',
        },
        {
          id: 'additional_social',
          label: 'Sosiale ferdigheter',
          description: 'Barnet trenger støtte og hjelp fra voksne til å trene på sosiale ferdigheter',
        },
        {
          id: 'additional_solo_care',
          label: 'Alene om omsorgen',
          description: 'Du er i stor grad alene om omsorgen for barnets hjelpebehov og/eller har lite familie og nettverk til å hjelpe deg',
        },
        {
          id: 'additional_mental_strain',
          label: 'Psykisk belastning',
          description: 'Barnets hjelpebehov medfører en psykisk belastning på deg',
        },
        {
          id: 'additional_other_tasks',
          label: 'Andre gjøremål påvirkes',
          description: 'Barnets hjelpebehov går ut over andre gjøremål i husstanden, for eksempel at du må vente med egenomsorg, handling eller husarbeid til barnet har lagt seg eller er på skolen.',
        },
        {
          id: 'additional_dominates',
          label: 'Dominerer hverdagen',
          description: 'Hjelpebehovet dominerer store deler av din dag eller begrenser din frihet',
        },
        {
          id: 'additional_own_condition',
          label: 'Egen tilstand',
          description: 'Du har en egen medisinsk tilstand som påvirker dine muligheter til å hjelpe barnet',
        },
        {
          id: 'additional_others_condition',
          label: 'Andres tilstand',
          description: 'Andre i husholdningen (foreldre eller søsken), har vansker som påvirker din tid til å hjelpe barnet',
        },
        {
          id: 'additional_risk',
          label: 'Risiko for skade',
          description: 'Barnet kan skade seg selv eller andre gjennom utagering, manglende forståelse, eller orientering',
        },
        {
          id: 'additional_communication',
          label: 'Kommunikasjon og språk',
          description: 'Barnet trenger ekstra hjelp i sin kommunikasjon og språkutvikling, sammenlignet med jevnaldrende',
        },
        {
          id: 'additional_holidays',
          label: 'Ferier og annerledesdager',
          description: 'Barnet trenger ekstra hjelp til å forberede seg til- og gjennomføre annerledesdager og/eller ferier er ekstra krevende.',
        },
      ],
      hasHelperFields: false,
      hasFreeText: true,
      freeTextPlaceholder: 'Har du eksempler på hvordan dette påvirker hverdagen? Jo mer konkret du beskriver, jo bedre blir søknaden. Tips! Beskriv også hva som skjer hvis du ikke utfører hjelpen.',
    },

    // =========================================================================
    // STEP 7: Sammendrag
    // =========================================================================
    {
      id: 'summary',
      title: 'Sammendrag',
      instruction: 'Se over valgene dine før du går videre.',
      gateQuestion: null,
      checkboxItems: [],
      hasHelperFields: false,
      hasFreeText: false,
      isSummaryStep: true,
    },
  ],

  helperOptions: [
    { value: 'mor', label: 'Mor' },
    { value: 'far', label: 'Far' },
    { value: 'begge', label: 'Begge' },
    { value: 'annen', label: 'Annen' },
  ],

  generationPrompt: '', // Will be added in Session 4
  outputSections: ['morning', 'daytime', 'evening', 'night', 'additional'],
};

export default hjelpestonadConfig;
